import React, { useEffect, useRef, useState } from 'react';
import jsQR from 'jsqr';
import { Camera, X, Link as LinkIcon, AlertCircle, Loader2 } from 'lucide-react';

/**
 * QR Scanner modal — uses the device camera + jsqr to decode. Falls back to
 * a paste-URL input when camera permission is denied or unavailable (desktop).
 *
 * Props:
 *   onResult(qrString)  – called with the raw decoded string
 *   onClose()
 */
export default function QRScannerModal({ onResult, onClose }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(0);
  const [error, setError] = useState('');
  const [pasteUrl, setPasteUrl] = useState('');
  const [useFallback, setUseFallback] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!navigator.mediaDevices?.getUserMedia) {
        setUseFallback(true);
        setError('This device cannot access a camera. Paste the link instead.');
        return;
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment' },
          audio: false,
        });
        if (cancelled) { stream.getTracks().forEach(t => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
          scanTick();
        }
      } catch (e) {
        setUseFallback(true);
        setError(e?.name === 'NotAllowedError'
          ? 'Camera permission denied. Paste the doctor’s link below instead.'
          : 'Could not start the camera. Paste the link below.');
      }
    })();
    return () => {
      cancelled = true;
      cancelAnimationFrame(rafRef.current);
      streamRef.current?.getTracks().forEach(t => t.stop());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scanTick = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || video.readyState < video.HAVE_ENOUGH_DATA) {
      rafRef.current = requestAnimationFrame(scanTick);
      return;
    }
    const w = canvas.width = video.videoWidth;
    const h = canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(video, 0, 0, w, h);
    try {
      const img = ctx.getImageData(0, 0, w, h);
      const code = jsQR(img.data, w, h, { inversionAttempts: 'dontInvert' });
      if (code?.data) {
        streamRef.current?.getTracks().forEach(t => t.stop());
        onResult(code.data);
        return;
      }
    } catch { /* jsqr can throw on tainted canvases; ignore */ }
    rafRef.current = requestAnimationFrame(scanTick);
  };

  const submitPasted = () => {
    if (!pasteUrl.trim()) { setError('Paste or type a doctor’s DocSite URL'); return; }
    onResult(pasteUrl.trim());
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/80 flex items-center justify-center p-4" role="dialog" aria-modal="true" data-testid="qr-scanner-modal">
      <div className="bg-white rounded-3xl max-w-md w-full p-5 shadow-2xl">
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-sky-100 text-sky-700 text-[10px] font-bold mb-2">
              <Camera size={10} /> SCAN DOCTOR QR
            </div>
            <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Point at your doctor’s poster
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">We’ll auto-save the clinic to your list.</p>
          </div>
          <button onClick={onClose} data-testid="qr-scanner-close" aria-label="Close"
                  className="text-slate-400 hover:text-slate-600 p-1">
            <X size={18} />
          </button>
        </div>

        {!useFallback && (
          <div className="relative rounded-2xl overflow-hidden bg-slate-900 aspect-square">
            <video ref={videoRef} playsInline muted className="w-full h-full object-cover" />
            <canvas ref={canvasRef} className="hidden" />
            <div className="absolute inset-6 border-2 border-white/60 rounded-2xl pointer-events-none" />
            <div className="absolute bottom-3 left-0 right-0 flex items-center justify-center gap-1.5 text-white text-xs">
              <Loader2 size={12} className="animate-spin" /> Looking for QR…
            </div>
          </div>
        )}

        {error && (
          <div className="mt-3 flex items-start gap-2 p-3 rounded-xl bg-amber-50 text-amber-800 text-xs" data-testid="qr-scanner-error">
            <AlertCircle size={14} className="flex-shrink-0 mt-0.5" /> <span>{error}</span>
          </div>
        )}

        <div className="mt-4">
          <p className="text-[11px] uppercase tracking-wider font-bold text-slate-400 mb-1.5">Or paste the link</p>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2 flex-1 rounded-xl border border-slate-200 px-3 py-2">
              <LinkIcon size={14} className="text-slate-400" />
              <input
                type="url"
                className="flex-1 bg-transparent text-sm outline-none"
                placeholder="https://docsite.care/dr/…"
                value={pasteUrl}
                onChange={(e) => setPasteUrl(e.target.value)}
                data-testid="qr-scanner-paste-input"
              />
            </div>
            <button onClick={submitPasted} data-testid="qr-scanner-paste-submit"
                    className="px-4 py-2 rounded-full bg-sky-600 text-white text-xs font-semibold hover:bg-sky-700">
              Go
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
